// src/pages/leads/EditLeadPage.tsx
import { useCallback, useMemo } from "react";

import AdminLayout from "../../layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";

import EditLeadForm from "@/components/leads/forms/EditLeadForm";
import { getAuth } from "@/api/client";

import { useEditLeadPage } from "./useEditLeadPage";
import type { LeadWithProducts } from "@/api/features/leads";

export default function EditLeadPage() {
  const authUser = getAuth().user as any;
  const isAffiliate = String(authUser?.roleName ?? "").trim() === "Affiliate Partner";
  const {
    // identifiers
    invalidId,

    // data
    lead,
    isLoading,
    isError,

    // form
    form,
    setForm,
    errorMessage,
    setErrorMessage,

    // dropdown options (global)
    productOptions,
    employeeOptions,
    STATUS_OPTIONS,
    SOURCE_OPTIONS,
    PRIORITY_OPTIONS,

    // mutations
    isSaving,
    isTransferring,
    isVisaDetailsSaving,

    // actions
    handleSave,
    handleCancel,

    // transfer state
    transferProductId,
    setTransferProductId,
    transferToUserId,
    setTransferToUserId,
    transferToProductId,
    setTransferToProductId,
    transferReasonKey,
    setTransferReasonKey,
    transferReasonText,
    setTransferReasonText,
    handleTransfer,
    visaLeadProductId,
    setVisaLeadProductId,
    visaProductOptions,
    visaDetailsDraft,
    setVisaDetailsDraft,

    // timeline
    timeline,
  } = useEditLeadPage();

  // Patch-based form change handler for the form component
  const handleFormChange = useCallback(
    (patch: Partial<typeof form>) => {
      if (!form) return;
      setForm((prev) => (prev ? { ...prev, ...patch } : prev));
      if (errorMessage) setErrorMessage(undefined);
    },
    [form, setForm, errorMessage, setErrorMessage]
  );

  // Options for "which product line to transfer?"
  // These are *per-lead* product lines (lead_product rows), not global products.
  const transferProductOptions = useMemo(() => {
    if (!lead) return [];
    const deep = lead as LeadWithProducts;

    return (deep.products ?? []).map((p) => ({
      value: String(p.id), // lead_product id
      label: p.productName ?? `Product #${p.productId}`,
    }));
  }, [lead]);

  /* ------------------------------- Top-level states ------------------------------- */

  let content: React.ReactNode = null;

  if (invalidId) {
    content = (
      <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        Invalid lead id.
      </div>
    );
  } else if (isLoading && !form) {
    content = (
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-4 text-sm text-gray-600">
        Loading lead…
      </div>
    );
  } else if (isError && !form) {
    content = (
      <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        Failed to load lead. Please try again.
      </div>
    );
  } else if (!form) {
    content = (
      <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
        Lead not found.
      </div>
    );
  } else {
    content = (
      <EditLeadForm
        // main form
        value={form}
        onChange={handleFormChange}
        errorMessage={errorMessage}
        submitting={isSaving}
        onSubmit={handleSave}
        onCancel={handleCancel}
        productOptions={productOptions}
        employeeOptions={employeeOptions}
        statusOptions={STATUS_OPTIONS}
        sourceOptions={SOURCE_OPTIONS}
        priorityOptions={PRIORITY_OPTIONS}
        // transfer section
        transferProductOptions={transferProductOptions}
      transferProductId={transferProductId}
      onChangeTransferProductId={setTransferProductId}
      transferToUserId={transferToUserId}
      onChangeTransferToUserId={setTransferToUserId}
      transferToProductId={transferToProductId}
      onChangeTransferToProductId={setTransferToProductId}
      transferReasonKey={transferReasonKey}
      onChangeTransferReasonKey={setTransferReasonKey}
      transferReasonText={transferReasonText}
      onChangeTransferReasonText={setTransferReasonText}
        onTransfer={handleTransfer}
        transferring={isTransferring}
        canManageTransfers={!isAffiliate}
        isVisaDetailsSaving={isVisaDetailsSaving}
        visaLeadProductId={visaLeadProductId}
        onChangeVisaLeadProductId={setVisaLeadProductId}
        visaProductOptions={visaProductOptions}
        visaDetailsDraft={visaDetailsDraft}
        onChangeVisaDetailsDraft={setVisaDetailsDraft}
      // timeline
      timeline={timeline}
      />
    );
  }

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Edit Lead</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-5 sm:px-6 sm:py-6">
        {content}
      </div>
    </AdminLayout>
  );
}
